import 'reflect-metadata';
import { jsonObject, jsonMember, toJson, jsonArrayMember } from 'typedjson';
import { Crop, Days, Position } from './enums';

export enum StabilizationInterpol {
  bilinear = 'bilinear',
  bicubic = 'bicubic',
  lanczos = 'lanczos',
}

export enum StabilizationCrop {
  keep = 'keep',
  black = 'black',
  none = 'none',
  fixed = 'fixed',
  expand = 'expand',
}

export enum TemporalFilterMode {
  none = 'none',
  tmix = 'tmix',
  center3 = 'center3',
  tmedian = 'tmedian',
}

@jsonObject
@toJson
export class IntervalDate {
  @jsonMember public begin!: number;
  @jsonMember public end!: number;
}

@jsonObject
@toJson
export class IntervalTime {
  @jsonMember public startHour!: string;
  @jsonMember public endHour!: string;
}

@jsonObject
export class ImageModifier {
  @jsonMember public iso!: number;
  @jsonMember public brightness!: number;
  @jsonMember public contrast!: number;
  @jsonMember public saturation!: number;
  @jsonMember public white!: number;
  @jsonMember({ constructor: String }) public crop!: Crop;
  @jsonMember public erigeWatermark!: boolean;
  @jsonMember public customWatermarkPicture?: string;
  @jsonMember({ constructor: String }) public watermarkPosition!: Position;
}

@jsonObject
export class TemporalFilterConfig {
  @jsonMember({ constructor: Boolean }) public enabled = false;
  @jsonMember({ constructor: String }) public mode?: TemporalFilterMode;

  // bords
  @jsonMember({ constructor: Boolean }) public tpad?: boolean;
  @jsonMember public tpadFrames?: number;

  // tmix
  @jsonMember public tmixFrames?: number;
  @jsonMember public tmixWeights?: string; // "1 1 1"
  @jsonMember public tmixPlanes?: number; // 1=luma, 7=YUV
  @jsonMember public tmixScale?: number;

  // center3
  @jsonMember public blendPlanes?: string; // "Y" | "YUV"

  // tmedian
  @jsonMember public tmedianFrames?: number; // impair
  @jsonMember public tmedianPlanes?: number; // 1=luma
}

@jsonObject
@toJson
export class StabilizationConfig {
  // === STAB ON/OFF ===
  @jsonMember({ constructor: Boolean }) public enabled = false;

  // === Détection/estimation ===
  @jsonMember public topIgnoreRatio?: number;
  @jsonMember public useSiftIfAvailable?: boolean;
  @jsonMember public nFeaturesSift?: number;
  @jsonMember public ratioTest?: number;
  @jsonMember public minMatchesForH?: number;
  @jsonMember public minMatchesForAffine?: number;
  @jsonMember public ransacReproj?: number;
  @jsonMember public ransacConf?: number;
  @jsonMember public ransacIters?: number;

  // === Stabilisation finale ===
  @jsonMember public percentile?: number;
  @jsonMember public analyzeDownscale?: number;
  @jsonMember public resizeOutputMaxW?: number;
  @jsonMember public useCapFfmpeg?: boolean;

  // === Filtre temporel ===
  @jsonMember({ constructor: TemporalFilterConfig }) public temporal?: TemporalFilterConfig;

  // === CLAHE ===
  @jsonMember({ constructor: Boolean }) public claheEnabled?: boolean;
  @jsonMember public claheClip?: number;
  @jsonMember public claheGrid?: number;
  @jsonMember public claheBlend?: number;

  // === Ken Burns (aplati) ===
  @jsonMember({ constructor: Boolean }) public kenburnsEnabled?: boolean;

  // start rect (0..1)
  @jsonMember public kbStartX?: number;
  @jsonMember public kbStartY?: number;
  @jsonMember public kbStartW?: number;
  @jsonMember public kbStartH?: number;

  // end rect (0..1)
  @jsonMember public kbEndX?: number;
  @jsonMember public kbEndY?: number;
  @jsonMember public kbEndW?: number;
  @jsonMember public kbEndH?: number;

  // easing
  @jsonMember public kbEasing?: string;

  //ratio mode
  @jsonMember public outARMode?: string;
  @jsonMember public outARCustom?: string;

  // === Intro / Outro
  @jsonMember public introKey?: string; // ex: "public/owner/project/camera/intro.png"
  @jsonMember public outroKey?: string; // ex: "public/owner/project/camera/outro.png"
  @jsonMember public introDurationMs?: number; // ex: 2000
  @jsonMember public outroDurationMs?: number;
  @jsonMember({ constructor: Boolean }) public introOutroEnabled?: boolean;

  //Musique
  @jsonMember({ constructor: Boolean }) public musicEnabled!: boolean; // default false côté UI
  @jsonMember public musicUrl?: string; // URL signée ou https public
  @jsonMember public musicTitle?: string; // purement informatif (UI)
  @jsonMember public musicGainDb?: number; // optionnel: niveau export (dB)
  @jsonMember public musicFadeInMs?: number; // optionnel: fondu entrée
  @jsonMember public musicFadeOutMs?: number; // optionnel: fondu sortie
  @jsonMember public musicLoop?: boolean; // optionnel: boucler si < vidéo
  @jsonMember public musicStartMs?: number;
  @jsonMember public musicEndMs?: number;

  // Sécurité
  @jsonMember public timeoutMs?: number;
}

@jsonObject
@toJson
export class TimelapseConfiguration extends ImageModifier {
  @jsonMember public interval!: IntervalDate;
  @jsonMember public lastNDays!: boolean;
  @jsonMember public lastNDaysSpan!: number;
  @jsonMember public period!: IntervalTime;
  @jsonArrayMember(IntervalTime) public exclude?: IntervalTime[];
  @jsonArrayMember(Number) public days!: Days[];
  @jsonMember public fps!: number;
  @jsonMember public hdr!: boolean;
  @jsonArrayMember(Number) public blue!: number[];
  @jsonArrayMember(Number) public gain!: number[];
  @jsonArrayMember(Number) public exposure!: number[];
  @jsonArrayMember(Number) public movement!: number[];
  @jsonArrayMember(Number) public goutte!: number[];
  @jsonArrayMember(Number) public clearness!: number[];
  @jsonArrayMember(Number) public person!: number[];
  @jsonArrayMember(String) public photoExclusion!: string[];
  @jsonMember public slowmotion!: boolean;
  @jsonMember public outputWidth!: number;

  @jsonMember({ constructor: StabilizationConfig }) public stabilization?: StabilizationConfig;
}
